/**
 * Modal Alert Action - WHAT Section
 *
 * Edit mode UI for scheduling an app alert (modal).
 * Used inline within AutomationCard when editing the "What" section.
 *
 * V2 Reference: v2/app/templates/components/client-reminder-modal-alert.hbs
 *
 * Fields:
 * - reminderOnId: Selected modal alert ID
 * - reminderOnType: 'ModalAlert' (set automatically)
 */

import { useQuery } from '@tanstack/react-query';
import { useFormikContext } from 'formik';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Stack } from 'src/components/Stack';
import { fetchAppAlerts } from 'src/models/conversation/conversation';
import { FormSelect } from 'src/shared/Forms/Fields/FormSelect';

import { ActionWhatSectionProps } from '../automationActions';

/**
 * ModalAlert WHAT Section - Edit mode
 */
export const ModalAlertWhatSection: FC<ActionWhatSectionProps> = ({
  index,
}) => {
  const { t } = useTranslation();
  const { setFieldValue } = useFormikContext();

  // Fetch available modal alerts
  const { data, isLoading } = useQuery({
    queryKey: ['modal-alerts'],
    queryFn: () => fetchAppAlerts(),
    staleTime: 60000,
  });

  const alerts = data?.alerts || [];
  const alertOptions = alerts.map((a) => ({
    value: a.id.toString(),
    label: a.title,
  }));

  const handleAlertChange = (alertId: string) => {
    setFieldValue(`automations.${index}.reminderOnId`, alertId);
    setFieldValue(`automations.${index}.reminderOnType`, 'ModalAlert');
  };

  return (
    <Stack spacing={2}>
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Box sx={{ minWidth: 200 }}>
          <FormSelect
            name={`automations.${index}.reminderOnId`}
            options={alertOptions}
            prompt={isLoading ? t('automations.common.loading') : t('automations.modalAlert.selectAppAlert')}
            onChange={(e) => handleAlertChange(e.target.value as string)}
          />
        </Box>
      </Box>
    </Stack>
  );
};

/**
 * ModalAlert What Summary Component
 */
export const ModalAlertWhatSummary: FC<{
  automation: ActionWhatSectionProps['automation'];
}> = ({ automation }) => {
  const { t } = useTranslation();
  const { data } = useQuery({
    queryKey: ['modal-alerts'],
    queryFn: () => fetchAppAlerts(),
    staleTime: 60000,
  });

  const alerts = data?.alerts || [];

  if (!automation.reminderOnId) {
    return <i>{t('automations.modalAlert.noAppAlertSelected')}</i>;
  }

  const alert = alerts.find(
    (a) => a.id.toString() === automation.reminderOnId?.toString()
  );
  const alertTitle = alert?.title || `Alert #${automation.reminderOnId}`;

  return <>{alertTitle}</>;
};
